import { getLocale } from "../locale";
import {
  averageDaysInMonth,
  yearInMs,
  averageDaysInYear,
  hourInMs,
  minuteInMs,
  dayInMs,
  weekInMS
} from "./constants";
import { removeFormat, parseFormatString } from "./utils";

const locale = getLocale().split("-")[0];

function formatDuration(ms, customFormat, options) {
  const totalHours = Math.floor(ms / hourInMs);
  const minutes = Math.floor((ms / minuteInMs) % 60);
  const seconds = Math.floor((ms / 1000) % 60);

  const pluginFormat = customFormat.includes("_");

  if (customFormat && !pluginFormat) {
    let formattedDuration = customFormat;
    const trimUnit = options?.stopTrim;

    if (trimUnit) {
      if (
        totalHours === 0 &&
        (trimUnit.includes("m") || trimUnit.includes("s"))
      ) {
        formattedDuration = removeFormat("h", formattedDuration);
      }
      if (minutes === 0 && trimUnit.includes("s")) {
        formattedDuration = removeFormat("m", formattedDuration);
      }
    }
    formattedDuration = formattedDuration
      .replace("hh", String(totalHours).padStart(2, "0"))
      .replace("h", String(totalHours))
      .replace("mm", String(minutes).padStart(2, "0"))
      .replace("m", String(minutes))
      .replace("ss", String(seconds).padStart(2, "0"));

    return formattedDuration;
  } else {
    const years = Math.floor(ms / (1000 * 3600 * 24 * 365.25));
    const remainingAfterYears = ms % (1000 * 3600 * 24 * 365.25);

    const months = Math.floor(remainingAfterYears / (1000 * 3600 * 24 * 30.44));
    const remainingAfterMonths =
      remainingAfterYears % (1000 * 3600 * 24 * 30.44);

    const days = Math.floor(remainingAfterMonths / (1000 * 3600 * 24));
    const remainingAfterDays = remainingAfterMonths % (1000 * 3600 * 24);

    const hours = Math.floor((remainingAfterDays / (1000 * 3600)) % 24);

    const minutes = Math.floor((remainingAfterDays / (1000 * 60)) % 60);

    const seconds = Math.floor((remainingAfterDays / 1000) % 60);
    let result = "";
    const unitDisplays = parseFormatString(customFormat);
    let maxUnitsToDisplay = options?.largest || false;

    const displayYears = customFormat ? customFormat.includes("y") : true;
    const displayMonths = customFormat ? customFormat.includes("M") : true;

    const displayDays = customFormat ? customFormat.includes("d") : true;
    const displayHours = customFormat ? customFormat.includes("h") : true;
    const displayMinutes = customFormat ? customFormat.includes("m") : true;
    const displaySeconds = customFormat ? customFormat.includes("s") : true;

    const commonFormats = {
      style: "unit"
    };

    const secondFormatter = new Intl.NumberFormat(locale, {
      ...commonFormats,
      unit: "second",
      unitDisplay: unitDisplays["s"] || "narrow"
    });
    const minuteFormatter = new Intl.NumberFormat(locale, {
      ...commonFormats,
      unit: "minute",
      unitDisplay: unitDisplays["m"] || "narrow"
    });
    const hourFormatter = new Intl.NumberFormat(locale, {
      ...commonFormats,
      unit: "hour",
      unitDisplay: unitDisplays["h"] || "narrow"
    });
    const dayFormatter = new Intl.NumberFormat(locale, {
      ...commonFormats,
      unit: "day",
      unitDisplay: "long"
    });
    const monthFormatter = new Intl.NumberFormat(locale, {
      ...commonFormats,
      unit: "month",
      unitDisplay: "long"
    });
    const yearFormatter = new Intl.NumberFormat(locale, {
      ...commonFormats,
      unit: "year",
      unitDisplay: "long"
    });

    if (displayYears) {
      if (years) result += `${yearFormatter.format(years)} `;
      if (maxUnitsToDisplay && maxUnitsToDisplay > 0) maxUnitsToDisplay -= 1;
      if (options?.onlyLargest && years) return result.trim();
    }
    if (displayMonths) {
      if (months) result += `${monthFormatter.format(months)} `;
      if (maxUnitsToDisplay && maxUnitsToDisplay > 0) maxUnitsToDisplay -= 1;
      if (options?.onlyLargest && months) return result.trim();
    }

    if (days > 0 && displayDays) {
      if (days) result += `${dayFormatter.format(days)} `;
      if (maxUnitsToDisplay && maxUnitsToDisplay > 0) maxUnitsToDisplay -= 1;
      if (options?.onlyLargest && days) return result.trim();
    }
    if (hours > 0 && displayHours) {
      if (maxUnitsToDisplay && maxUnitsToDisplay === 0) return result.trim();
      if (hours)
        result += `${hourFormatter.format(
          pluginFormat && !customFormat.includes("d")
            ? Math.floor(ms / 1000 / 60 / 60)
            : hours
        )} `;
      if (maxUnitsToDisplay && maxUnitsToDisplay > 0) maxUnitsToDisplay -= 1;
      if (options?.onlyLargest && hours) return result.trim();
    }
    if (minutes > 0 && displayMinutes) {
      if (maxUnitsToDisplay && maxUnitsToDisplay === 0) return result.trim();
      if (minutes)
        result += `${minuteFormatter.format(
          pluginFormat && !customFormat.includes("h")
            ? Math.floor(ms / 1000 / 60)
            : minutes
        )} `;
      if (maxUnitsToDisplay && maxUnitsToDisplay > 0) maxUnitsToDisplay -= 1;
      if (options?.onlyLargest && minutes) return result.trim();
    }
    if (
      seconds > 0 &&
      ((hours < 1 && displaySeconds) || customFormat.includes("s"))
    ) {
      if (maxUnitsToDisplay === 0) return result.trim();
      result += `${secondFormatter.format(
        pluginFormat && !customFormat.includes("m")
          ? Math.floor(ms / 1000)
          : seconds
      )}`;
    }

    return result.trim();
  }
}

const format = (date, formatString, intlOptions) => {
  if (formatString === "LT") {
    const options = {
      hour: "numeric",
      minute: "numeric"
    };
    return new Intl.DateTimeFormat(undefined, options).format(date);
  }
  const options = {
    year: formatString.includes("YYYY") ? "numeric" : undefined,
    month: formatString.includes("MM") ? "2-digit" : undefined,
    day: formatString.includes("DD") ? "2-digit" : undefined,
    hour: formatString.includes("HH") ? "2-digit" : undefined,
    minute: formatString.includes("m")
      ? formatString.includes("mm")
        ? "2-digit"
        : "numeric"
      : undefined,
    second: formatString.includes("ss") ? "2-digit" : undefined,
    ...(intlOptions && intlOptions)
  };
  const formatter = new Intl.DateTimeFormat(locale, options);
  const parts = formatter.formatToParts(date);
  let formattedDate = formatString
    .replace("YYYY", parts.find((part) => part.type === "year")?.value || "")
    .replace("MM", parts.find((part) => part.type === "month")?.value || "")
    .replace("DD", parts.find((part) => part.type === "day")?.value || "")
    .replace(
      "HH",
      normalizeHours(
        parts.find((part) => part.type === "hour")?.value || "",
        intlOptions
      )
    )
    .replace("h", parts.find((part) => part.type === "hour")?.value || "")
    .replace("mm", parts.find((part) => part.type === "minute")?.value || "")
    .replace("ss", parts.find((part) => part.type === "second")?.value || "");
  const dayPeriod =
    parts.find((part) => part.type === "dayPeriod")?.value || "";
  if (intlOptions?.hour12 !== false && dayPeriod)
    formattedDate += ` ${dayPeriod}`;
  return formattedDate;
};
const normalizeHours = (hours, options) => {
  if (hours === "24" && options?.hour12 === false) hours = "00";
  return hours;
};

const parseAndFormat = (string, _format) => {
  const [hours, minutes] = string.split(":").map(Number);

  const date = new Date();
  date.setHours(hours, minutes);

  return format(date, _format);
};

const diff = (dateFrom, to) => {
  return dateFrom.getTime() - new Date(to).getTime();
};

const getDayOfYear = (date) => {
  const startOfYear = new Date(date.getFullYear(), 0, 0);
  const diff = date - startOfYear;
  return Math.floor(diff / dayInMs);
};

const add = (date, ms) => {
  const newDate = new Date(date);
  newDate.setTime(date.getTime() + ms);

  return new EnhancedDate(newDate);
};

const set = (date, values) => {
  const newDate = new Date(date);
  if (values["hour"] !== undefined) newDate.setHours(values["hour"]);
  if (values["minute"] !== undefined) newDate.setMinutes(values["minute"]);
  return new EnhancedDate(newDate);
};

const isBetween = (date, min, max) => {
  return new Date(date) >= new Date(min) && new Date(date) <= new Date(max);
};
function humanizeDuration(ms) {
  const units = [
    { unit: "year", ms: yearInMs },
    { unit: "month", ms: 1000 * 60 * 60 * 24 * 30 },
    { unit: "week", ms: weekInMS },
    { unit: "day", ms: dayInMs },
    { unit: "hour", ms: hourInMs },
    { unit: "minute", ms: minuteInMs },
    { unit: "second", ms: 1000 }
  ];

  const isPast = ms < 0;
  ms = Math.abs(ms);
  const rtf = new Intl.RelativeTimeFormat(locale);

  for (const { unit, ms: unitMs } of units) {
    if (ms >= unitMs || unit === "second") {
      const amount = Math.round(ms / unitMs);
      return isPast ? rtf.format(-amount, unit) : rtf.format(amount, unit);
    }
  }
}
function time(date = new Date()) {
  const dateObj = new Date(date);

  return new EnhancedDate(dateObj);
}

time.duration = (ms) => {
  return {
    format: (customFormat, options) =>
      formatDuration(ms, customFormat, options),
    humanize: () => humanizeDuration(ms),
    asMilliseconds: () => ms,
    asSeconds: () => Math.floor(ms / 1000),
    asDays: () => Math.floor(ms / dayInMs),
    asMonths: () => Math.floor(ms / dayInMs / averageDaysInMonth),
    asYears: () => Math.floor(ms / dayInMs / averageDaysInYear)
  };
};

time.monthName = (date) =>
  new Intl.DateTimeFormat(locale, { month: "long" }).format(date);

time.humanizeUnit = (value, unit) => {
  const formatter = new Intl.NumberFormat(locale, {
    style: "unit",
    unit: unit,
    unitDisplay: "long"
  });
  return formatter.format(value);
};

time.displayDate = (date, options) => {
  if (options && date) {
    return new Intl.DateTimeFormat(locale, options).format(new Date(date));
  }
};
class EnhancedDate extends Date {
  constructor(dateObj) {
    super(dateObj);
    this.dateObj = dateObj;
  }
  format = (formatString, intlOptions) =>
    format(this.dateObj, formatString, intlOptions);
  parseAndFormat = (string, formatString) =>
    parseAndFormat(string, formatString);
  year = () => this.dateObj.getFullYear();
  month = () => this.dateObj.getMonth();
  day = () => this.dateObj.getDay();
  dayOfYear = () => getDayOfYear(this.dateObj);
  diff = (to) => diff(this.dateObj, to);
  add = (ms) => add(this.dateObj, ms);
  set = (values) => set(this.dateObj, values);
  isBetween = (min, max) => isBetween(this.dateObj, min, max);
  fromNow = () =>
    humanizeDuration(this.dateObj.getTime() - new Date().getTime());
  asString = () => this.dateObj.toDateString();
  asMilliseconds = () => this.dateObj.getTime();
}

export default time;
